import { makeAutoObservable, runInAction } from 'mobx';
import { apiFetch } from '../api/apiClient';
import { getAPIUrl } from '../api/apiUtils';


class TimelineEventsStore {
  events = [];
  loading = false;
  loaded = false;
  saving = false;
  error = '';

  constructor() {
    makeAutoObservable(this);
  }

  get myEvents() {
    return this.events.filter((event) => event.is_owner);
  }

  get otherEvents() {
    return this.events.filter((event) => !event.is_owner);
  }

  async load({ force = false } = {}) {
    if (this.loading || (this.loaded && !force)) return;
    this.loading = true;
    this.error = '';
    try {
      const response = await apiFetch(`${getAPIUrl()}/timeline_events/`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Не удалось загрузить события');
      }
      runInAction(() => {
        this.events = [...(payload.my_events || []), ...(payload.other_events || [])]
          .sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title));
        this.loaded = true;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Не удалось загрузить события';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async save(values, eventId = null) {
    this.saving = true;
    this.error = '';
    try {
      const response = await apiFetch(
        eventId
          ? `${getAPIUrl()}/timeline_events/${eventId}`
          : `${getAPIUrl()}/timeline_events/`,
        {
          method: eventId ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(values),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const fieldError = payload.fields && Object.values(payload.fields)[0];
        throw new Error(fieldError || payload.error || 'Не удалось сохранить событие');
      }
      runInAction(() => {
        const index = this.events.findIndex((event) => event.id === payload.id);
        if (index >= 0) this.events[index] = payload;
        else this.events.push(payload);
        this.events = [...this.events]
          .sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title));
      });
      return payload;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Не удалось сохранить событие';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  async remove(eventId) {
    this.saving = true;
    this.error = '';
    try {
      const response = await apiFetch(`${getAPIUrl()}/timeline_events/${eventId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Не удалось удалить событие');
      }
      runInAction(() => {
        this.events = this.events.filter((event) => event.id !== eventId);
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Не удалось удалить событие';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }
}

const timelineEventsStore = new TimelineEventsStore();

export default timelineEventsStore;
